import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useSearchParams } from 'react-router-dom';
import { IonSpinner, IonIcon } from '@ionic/react';
import { 
  analyticsOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  alertCircleOutline,
  timeOutline,
  documentOutline,
  imageOutline,
  videocamOutline,
  downloadOutline,
  closeOutline,
  arrowBackOutline,
  arrowForwardOutline,
  gitBranchOutline,
  arrowUndoOutline,
  ticketOutline
} from 'ionicons/icons';
import { Helmet } from 'react-helmet-async';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { useTicketExecutions } from '../../hooks/useExecutions';
import { useCreatePR } from '../../hooks/useCreatePR';
import { CodeDiffViewer } from '../../components/shared/CodeDiffViewer';
import TestCasesViewer from '../../components/shared/TestCasesViewer/TestCasesViewer';
import { useProjectData } from '../ProjectsPage/ProjectLayout';
import { computeWordDiff, escHtml } from '../../utils/wordDiff';
import TicketTitleRow from '../../components/shared/TicketTitleRow/TicketTitleRow';
import { splitCodeImplByRepo } from '../../utils/diffUtils';
import { extractSuggestedChanges } from '../../utils/ticketUtils';
import HoverPopover from '../../components/shared/HoverPopover/HoverPopover';
import DiffActionBtns, { diffActionBtnsHtml } from '../../components/shared/DiffActionBtns/DiffActionBtns';
import DescriptionDiffView from '../../components/shared/DescriptionDiffView/DescriptionDiffView';
import api, { API_BASE_URL, getAuthToken } from '../../services/api';
import './TicketDetailsPage.css';

// Format relative time (e.g., "5 min ago", "2 hours ago")
const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'number' ? new Date(dateInput) : new Date(dateInput);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Check if file is an image
const isImage = (mimeType) => mimeType?.startsWith('image/');
const isVideo = (mimeType) => mimeType?.startsWith('video/');

// Get proxy URL for Jira attachment
const getAttachmentUrl = (projectId, attachmentId, thumbnail = false) => {
  const params = thumbnail ? '?thumbnail=true' : '';
  return `${API_BASE_URL}/projects/${projectId}/attachments/${attachmentId}${params}`;
};

// Rewrite Jira attachment URLs in HTML to use our proxy
const rewriteAttachmentUrls = (html, projectId) => {
  if (!html) return html;
  let result = html;
  // Pattern 1: https://api.atlassian.com/ex/jira/{cloudId}/rest/api/3/attachment/content/{attachmentId}
  // Pattern 2: /rest/api/3/attachment/content/{attachmentId}
  result = result.replace(
    /(?:https:\/\/api\.atlassian\.com\/ex\/jira\/[^/]+)?\/rest\/api\/3\/attachment\/content\/(\d+)/g,
    (match, attachmentId) => `${API_BASE_URL}/projects/${projectId}/attachments/${attachmentId}`
  );
  // Pattern 3: https://{site}.atlassian.net/rest/api/3/attachment/content/{attachmentId}
  result = result.replace(
    /https:\/\/[^/]+\.atlassian\.net\/rest\/api\/3\/attachment\/content\/(\d+)/g,
    (match, attachmentId) => `${API_BASE_URL}/projects/${projectId}/attachments/${attachmentId}`
  );
  // Pattern 4: https://{site}.atlassian.net/secure/attachment/{attachmentId}/{filename}
  result = result.replace(
    /https:\/\/[^/]+\.atlassian\.net\/secure\/attachment\/(\d+)\/[^"'\s]*/g,
    (match, attachmentId) => `${API_BASE_URL}/projects/${projectId}/attachments/${attachmentId}`
  );
  // Pattern 5: https://{site}.atlassian.net/secure/thumbnail/{attachmentId}/{filename}
  result = result.replace(
    /https:\/\/[^/]+\.atlassian\.net\/secure\/thumbnail\/(\d+)\/[^"'\s]*/g,
    (match, attachmentId) => `${API_BASE_URL}/projects/${projectId}/attachments/${attachmentId}`
  );
  return result;
};

// Component to load authenticated images
const AuthImage = ({ src, alt, className, onError }) => {
  const [imageSrc, setImageSrc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(false);

    fetch(src, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load');
        return res.blob();
      })
      .then(blob => {
        if (isMounted) {
          setImageSrc(URL.createObjectURL(blob));
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError(true);
          setLoading(false);
          if (onError) onError();
        }
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (loading) {
    return <div className="auth-image-loading"><IonSpinner /></div>;
  }

  if (error || !imageSrc) {
    return (
      <div className="auth-image-error">
        <IonIcon icon={imageOutline} />
      </div>
    );
  }

  return <img src={imageSrc} alt={alt} className={className} />;
};

// Extract attachment proxy URLs from HTML
const extractProxyUrls = (html) => {
  if (!html) return [];
  const regex = /src="([^"]*\/projects\/[^"]*\/attachments\/[^"]*)"/gi;
  const urls = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    urls.push(match[1]);
  }
  return [...new Set(urls)];
};

// Component to render HTML content with authenticated images
const AuthHtmlContent = ({ html, projectId, className }) => {
  const [blobMap, setBlobMap] = React.useState({});
  const loadingRef = React.useRef(new Set());

  // Rewrite Jira URLs to proxy URLs
  const rewrittenHtml = React.useMemo(
    () => rewriteAttachmentUrls(html, projectId),
    [html, projectId]
  );

  // Find all proxy image URLs and fetch them with auth
  React.useEffect(() => {
    const proxyUrls = extractProxyUrls(rewrittenHtml);
    if (proxyUrls.length === 0) return;

    proxyUrls.forEach(async (url) => {
      // Skip if already loaded or currently loading
      if (blobMap[url] || loadingRef.current.has(url)) return;
      loadingRef.current.add(url);

      try {
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setBlobMap(prev => ({ ...prev, [url]: blobUrl }));
        }
      } catch (err) {
        console.error('Failed to load inline image:', url, err);
      } finally {
        loadingRef.current.delete(url);
      }
    });
  }, [rewrittenHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // Replace proxy URLs with blob URLs in the HTML, then sanitize
  const PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  const finalHtml = React.useMemo(() => {
    let result = rewrittenHtml;
    // Replace loaded images with blob URLs
    Object.entries(blobMap).forEach(([proxyUrl, blobUrl]) => {
      result = result.split(proxyUrl).join(blobUrl);
    });
    // Replace any remaining proxy image URLs with transparent placeholder
    // to prevent the browser from making unauthenticated requests
    result = result.replace(
      /(<img[^>]*\s)src="(http[^"]*\/attachments\/[^"]*)"/gi,
      `$1src="${PLACEHOLDER}" data-pending="$2" style="min-height:80px;background:#2a2a2a;border-radius:6px;"`
    );
    return DOMPurify.sanitize(result, {
      ADD_TAGS: ['img'],
      ADD_ATTR: ['src', 'alt', 'width', 'height', 'loading', 'style', 'data-pending'],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|blob|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
    });
  }, [rewrittenHtml, blobMap]);

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
};

// Image Gallery / Lightbox component
const ImageGallery = ({ images, initialIndex, onClose, projectId }) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const [imageSrc, setImageSrc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const currentImage = images[currentIndex];

  // Load current image with auth
  React.useEffect(() => {
    if (!currentImage) return;
    
    setLoading(true);
    const url = getAttachmentUrl(projectId, currentImage.id);
    
    fetch(url, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` }
    })
      .then(res => res.blob())
      .then(blob => {
        setImageSrc(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [currentIndex, currentImage, projectId]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleDownload = async () => {
    try {
      const url = getAttachmentUrl(projectId, currentImage.id);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = currentImage.filename;
      a.click();
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  return (
    <div className="image-gallery-overlay" onClick={onClose}>
      <div className="image-gallery-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="gallery-header">
          <span className="gallery-filename">{currentImage?.filename}</span>
          <span className="gallery-counter">{currentIndex + 1} / {images.length}</span>
          <div className="gallery-actions">
            <button className="gallery-btn" onClick={handleDownload} title="Download">
              <IonIcon icon={downloadOutline} />
            </button>
            <button className="gallery-btn" onClick={onClose} title="Close">
              <IonIcon icon={closeOutline} />
            </button>
          </div>
        </div>

        {/* Main image area */}
        <div className="gallery-main">
          {images.length > 1 && (
            <button className="gallery-nav gallery-prev" onClick={goToPrev}>
              <IonIcon icon={arrowBackOutline} />
            </button>
          )}
          
          <div className="gallery-image-wrapper">
            {loading ? (
              <IonSpinner />
            ) : imageSrc ? (
              <img src={imageSrc} alt={currentImage?.filename} />
            ) : (
              <div className="gallery-error">Failed to load image</div>
            )}
          </div>

          {images.length > 1 && (
            <button className="gallery-nav gallery-next" onClick={goToNext}>
              <IonIcon icon={arrowForwardOutline} />
            </button>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="gallery-thumbnails">
            {images.map((img, index) => (
              <div 
                key={img.id}
                className={`gallery-thumb ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
              >
                <AuthImage 
                  src={getAttachmentUrl(projectId, img.id, true)}
                  alt={img.filename}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Extract text from ADF (Atlassian Document Format)
const extractAdfText = (adf) => {
  if (!adf) return '';
  if (typeof adf === 'string') return adf;
  
  const extract = (node) => {
    if (!node) return '';
    if (node.type === 'text') return node.text || '';
    if (node.content) return node.content.map(extract).join('');
    return '';
  };
  
  if (adf.content) {
    return adf.content.map(extract).join('\n');
  }
  return '';
};

const InlineSuggestion = ({ change, onAccept, onReject, disabled }) => {
  if (!change || change.status === 'rejected') return null;

  if (change.status === 'accepted') {
    return (
      <div className="inline-suggestion accepted">
        <div className="inline-suggestion-badge accepted">
          <IonIcon icon={checkmarkCircleOutline} />
          Kept
        </div>
      </div>
    );
  }

  const diffOps = computeWordDiff(change.original, change.suggested);

  return (
    <div className="inline-suggestion pending">
      <div className="inline-suggestion-inline-diff">
        <span className="inline-diff-body">
          {diffOps.map((op, idx) => {
            if (op.type === 'equal') {
              return <span key={idx} className="idiff-equal">{op.value}</span>;
            }
            if (op.type === 'removed') {
              return <span key={idx} className="idiff-removed">{op.value}</span>;
            }
            if (op.type === 'added') {
              return <span key={idx} className="idiff-added">{op.value}</span>;
            }
            return null;
          })}
        </span>
        <DiffActionBtns
          onAccept={() => onAccept(change.id)}
          onReject={() => onReject(change.id)}
          disabled={disabled}
          className="inline-diff-actions"
        />
      </div>
      {change.reasoning && (
        <div className="inline-suggestion-reasoning">
          {change.reasoning}
        </div>
      )}
    </div>
  );
};

// escHtml imported from shared wordDiff module

const buildInlineDiffHtml = (change) => {
  const ops = computeWordDiff(change.original, change.suggested);
  let body = '';
  for (const op of ops) {
    const v = escHtml(op.value);
    if (op.type === 'equal') body += `<span class="idiff-equal">${v}</span>`;
    else if (op.type === 'removed') body += `<span class="idiff-removed">${v}</span>`;
    else if (op.type === 'added') body += `<span class="idiff-added">${v}</span>`;
  }
  const cid = escHtml(change.id);
  const reasoning = change.reasoning
    ? `<div class="inline-suggestion-reasoning">${escHtml(change.reasoning)}</div>`
    : '';
  const btns = diffActionBtnsHtml({
    acceptAttrs: `data-change-action="keep" data-change-id="${cid}"`,
    rejectAttrs: `data-change-action="undo" data-change-id="${cid}"`,
    className: 'inline-diff-actions',
  });
  return `<div class="inline-suggestion pending"><div class="inline-suggestion-inline-diff"><span class="inline-diff-body">${body}</span>${btns}</div>${reasoning}</div>`;
};

const normalizeWs = (s) => (s || '').replace(/\s+/g, ' ').trim();

const BLOCK_SELECTORS = 'li, p, h1, h2, h3, h4, h5, h6, td, th, blockquote, dt, dd, figcaption';

const findTargetElement = (root, searchText) => {
  const norm = normalizeWs(searchText);
  if (!norm) return null;

  const candidates = root.querySelectorAll(BLOCK_SELECTORS);

  // Pass 1: exact match -- take first occurrence
  for (const el of candidates) {
    if (normalizeWs(el.textContent) === norm) {
      return { el, exact: true };
    }
  }

  // Pass 2: contains match -- deepest element that includes the text
  let best = null;
  for (const el of candidates) {
    if (normalizeWs(el.textContent).includes(norm)) {
      if (!best || best.contains(el)) best = el;
    }
  }

  if (best && best !== root) return { el: best, exact: false };
  return null;
};

const injectDiffIntoHtml = (html, changes) => {
  if (!html) return { html, injectedIds: [] };

  const pending = (changes || []).filter(c => c.status === 'pending' && c.original);
  if (pending.length === 0) return { html, injectedIds: [] };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstChild;
    const injectedIds = [];

    for (const change of pending) {
      const result = findTargetElement(root, change.original);
      if (!result) continue;

      const { el: target, exact } = result;
      const diffHtml = buildInlineDiffHtml(change);

      if (exact) {
        // Exact match: replace entire element content with diff
        const wrapper = doc.createElement(target.tagName.toLowerCase());
        if (target.className) wrapper.className = target.className + ' inline-diff-injected';
        else wrapper.className = 'inline-diff-injected';
        wrapper.innerHTML = diffHtml;
        target.parentNode.replaceChild(wrapper, target);
      } else {
        // Substring match: replace only the matching text portion,
        // keeping surrounding content intact
        const origText = change.original;
        const escaped = escHtml(origText);

        // Try replacing in innerHTML (handles text within child elements)
        const before = target.innerHTML;
        let replaced = false;

        // First try: literal text match in innerHTML
        if (before.includes(origText)) {
          target.innerHTML = before.replace(origText, diffHtml);
          replaced = true;
        }
        // Second try: escaped version (text might be entity-encoded in HTML)
        if (!replaced && before.includes(escaped)) {
          target.innerHTML = before.replace(escaped, diffHtml);
          replaced = true;
        }

        if (!replaced) continue;

        target.classList.add('inline-diff-injected');
      }

      injectedIds.push(change.id);
    }

    return { html: root.innerHTML, injectedIds };
  } catch (err) {
    console.error('injectDiffIntoHtml failed, falling back to unmodified HTML:', err);
    return { html, injectedIds: [] };
  }
};

// Simple markdown to HTML for description overrides
const mdToHtml = (md) => {
  if (!md) return '';
  const inlineFormat = (text) => {
    let s = escHtml(text);
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, '<em>$1</em>');
    s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
    return s;
  };

  const lines = md.split('\n');
  const html = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const listMatch = line.match(/^- (?:\[[ x]\] )?(.+)$/);

    if (listMatch) {
      if (!inList) { html.push('<ul>'); inList = true; }
      html.push(`<li>${inlineFormat(listMatch[1])}</li>`);
    } else {
      if (inList) { html.push('</ul>'); inList = false; }
      const trimmed = line.trim();
      if (trimmed === '') continue;
      html.push(`<p>${inlineFormat(trimmed)}</p>`);
    }
  }
  if (inList) html.push('</ul>');

  return html.join('\n');
};

const applyDescriptionOverrides = (html, overrides) => {
  if (!overrides?.description) return html || '';
  const desc = overrides.description;
  // Handle old format (string) - just a suggested value
  if (typeof desc === 'string') {
    const rendered = mdToHtml(desc);
    return html ? html + rendered : rendered;
  }
  // Handle new format ({ original, suggested })
  if (typeof desc === 'object' && desc.suggested) {
    const rendered = mdToHtml(desc.suggested);
    // Always use the fully rendered markdown-to-HTML version
    return rendered;
  }
  return html || '';
};


const DescriptionWithInlineDiff = ({ html, projectId, changes = [], onAccept, onReject, disabled, localOverrides }) => {
  const wrapperRef = useRef(null);

  // Apply accepted overrides to the HTML first, then inject pending diffs
  const htmlWithOverrides = useMemo(
    () => applyDescriptionOverrides(html, localOverrides),
    [html, localOverrides]
  );

  // Check for pending full-description change
  const descriptionChange = changes.find(c => c.field === 'description' && c.status === 'pending');
  const otherChanges = changes.filter(c => c.field !== 'description');

  const { html: processedHtml, injectedIds } = useMemo(
    () => injectDiffIntoHtml(htmlWithOverrides, descriptionChange ? otherChanges : changes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [htmlWithOverrides, JSON.stringify((descriptionChange ? otherChanges : changes).map(c => [c.id, c.original, c.suggested, c.status]))]
  );

  useEffect(() => {
    if (!wrapperRef.current) return;
    const handler = (e) => {
      const btn = e.target.closest('[data-change-action]');
      if (!btn || disabled) return;
      const action = btn.dataset.changeAction;
      const id = btn.dataset.changeId;
      if (action === 'keep') onAccept(id);
      else if (action === 'undo') onReject(id);
    };
    const el = wrapperRef.current;
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [onAccept, onReject, disabled]);

  // Any pending non-description changes that couldn't be injected inline
  const fallbackChanges = (descriptionChange ? otherChanges : changes)
    .filter(c => c.status === 'pending' && !injectedIds.includes(c.id));

  // Full-description change: render paragraph-level diff instead of normal description
  if (descriptionChange) {
    return (
      <div ref={wrapperRef}>
        <DescriptionDiffView
          original={descriptionChange.original}
          suggested={descriptionChange.suggested}
          changeId={descriptionChange.id}
          onAccept={onAccept}
          onReject={onReject}
          disabled={disabled}
          reasoning={descriptionChange.reasoning}
          className="ticket-description"
        />
      </div>
    );
  }

  return (
    <div ref={wrapperRef}>
      <AuthHtmlContent
        html={processedHtml}
        projectId={projectId}
        className="ticket-description"
      />
      {fallbackChanges.map(c => (
        <InlineSuggestion key={c.id} change={c} onAccept={onAccept} onReject={onReject} disabled={disabled} />
      ))}
    </div>
  );
};

const TicketDetailsPage = () => {
  const { projectId, ticketKey } = useParams();
  const projectData = useProjectData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Use React Query to fetch executions (analyses)
  const { 
    executions: analyses = [], 
    isLoading: analysesLoading 
  } = useTicketExecutions(projectId, ticketKey, 'analysis');
  
  // Gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  // Inline suggestion state
  const [suggestedChanges, setSuggestedChanges] = useState([]);
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [applyError, setApplyError] = useState(null);

  // Get initial tab from URL params, default to 'summary'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'summary');

  useEffect(() => {
    loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, ticketKey]);

  const loadTicket = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await api.get(`/projects/${projectId}/tickets/${ticketKey}`);
      setTicket(data);
      setLoading(false);

      const freshData = await api.get(`/projects/${projectId}/tickets/${ticketKey}?refresh=true`);
      setTicket(prev => {
        if (!prev) return freshData;
        const mergedOverrides = {
          ...(freshData.localOverrides || {}),
          ...(prev.localOverrides || {})
        };
        return {
          ...freshData,
          localOverrides: Object.keys(mergedOverrides).length > 0 ? mergedOverrides : freshData.localOverrides,
          appliedAnalysisId: prev.appliedAnalysisId || freshData.appliedAnalysisId,
        };
      });
    } catch (err) {
      console.error('Error loading ticket:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const latestAnalysis = analyses[0];
  const { isCreatingRepo, handleCreatePR } = useCreatePR({
    execution: latestAnalysis,
    fallbackTitle: `Code changes for ${ticketKey}`
  });

  // Sync suggested changes from latest analysis
  useEffect(() => {
    if (latestAnalysis && ticket) {
      const changes = extractSuggestedChanges(latestAnalysis, ticket, { pendingOnly: true });
      setSuggestedChanges(changes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestAnalysis?.executionId, ticket?.updatedAt]);

  const getFieldChange = useCallback((field) => {
    return suggestedChanges.find(c => c.field === field);
  }, [suggestedChanges]);

  const suggestedChangesRef = useRef(suggestedChanges);
  suggestedChangesRef.current = suggestedChanges;

  const saveChanges = useCallback(async (updatedChanges) => {
    if (!latestAnalysis || !projectId || !ticketKey) return;
    setApplyingChanges(true);
    setApplyError(null);
    try {
      const analysisId = latestAnalysis.executionId || latestAnalysis.id;
      const result = await api.post(
        `/projects/${projectId}/tickets/${ticketKey}/apply-changes`,
        {
          analysisId,
          changes: updatedChanges.map(c => {
            const entry = { id: c.id, status: c.status };
            if (c.field === 'description' && c.suggested !== undefined) {
              entry.suggestedOverride = c.suggested;
            }
            return entry;
          })
        }
      );
      setTicket(prev => prev ? {
        ...prev,
        localOverrides: result.localOverrides || prev.localOverrides,
        appliedAnalysisId: result.appliedAnalysisId || prev.appliedAnalysisId,
      } : prev);
      const remaining = updatedChanges.filter(c => c.status === 'pending');
      setSuggestedChanges(remaining);
    } catch (err) {
      console.error('Error applying changes:', err);
      setApplyError(err.message);
    } finally {
      setApplyingChanges(false);
    }
  }, [latestAnalysis, projectId, ticketKey]);

  const handleAcceptChange = useCallback((id, mergedText) => {
    const updated = suggestedChangesRef.current.map(c => {
      if (c.id !== id) return c;
      if (mergedText !== undefined && c.field === 'description') {
        return { ...c, status: 'accepted', suggested: mergedText };
      }
      return { ...c, status: 'accepted' };
    });
    setSuggestedChanges(updated);
    saveChanges(updated);
  }, [saveChanges]);

  const handleRejectChange = useCallback((id) => {
    const updated = suggestedChangesRef.current.map(c => c.id === id ? { ...c, status: 'rejected' } : c);
    setSuggestedChanges(updated);
    saveChanges(updated);
  }, [saveChanges]);

  const handleAcceptAll = useCallback(() => {
    const updated = suggestedChangesRef.current.map(c => c.status === 'pending' ? { ...c, status: 'accepted' } : c);
    setSuggestedChanges(updated);
    saveChanges(updated);
  }, [saveChanges]);

  const handleRejectAll = useCallback(() => {
    const updated = suggestedChangesRef.current.map(c => c.status === 'pending' ? { ...c, status: 'rejected' } : c);
    setSuggestedChanges(updated);
    saveChanges(updated);
  }, [saveChanges]);

  const hasPendingChanges = suggestedChanges.some(c => c.status === 'pending');

  const testCasesSaveTimer = useRef(null);
  const handleTestCasesChange = useCallback((updatedTestCases) => {
    if (testCasesSaveTimer.current) clearTimeout(testCasesSaveTimer.current);
    testCasesSaveTimer.current = setTimeout(async () => {
      try {
        const execId = analyses[0]?.executionId || analyses[0]?.id;
        if (!execId) return;
        await api.patch(`/executions/${execId}/test-cases`, { testCases: updatedTestCases });
      } catch (err) {
        console.error('Failed to save test cases:', err);
      }
    }, 800);
  }, [analyses]);

  // Handle tab change and persist to URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Determine effective status (check completedAt in case status field wasn't updated)
  const getEffectiveStatus = (analysis) => {
    const s = (analysis.status || '').toLowerCase();
    
    // Priority 1: Explicit status (most reliable)
    if (s === 'completed' || s === 'success') return 'completed';
    if (s === 'failed' || s === 'error') return 'failed';
    if (s === 'cancelled') return 'cancelled';
    if (s === 'blocked') return 'blocked';
    if (s === 'insufficient_context') return 'insufficient_context';
    
    // Priority 2: Check completedAt (fallback for status not updated)
    if (analysis.completedAt) {
      // If has completedAt, check if it's a real failure (not just ECS lifecycle messages)
      const isRealFailure = analysis.error && 
        !analysis.error.includes('Essential container in task exited') &&
        !analysis.error.includes('Task stopped');
      return isRealFailure ? 'failed' : 'completed';
    }
    
    // Priority 3: Return current status
    return s;
  };

  const getStatusIcon = (analysis) => {
    const s = getEffectiveStatus(analysis);
    if (s === 'completed' || s === 'success') {
      return <IonIcon icon={checkmarkCircleOutline} style={{ color: '#52c41a' }} />;
    }
    if (s === 'cancelled') {
      return <IonIcon icon={closeCircleOutline} style={{ color: '#f97316' }} />;
    }
    if (s === 'failed' || s === 'error') {
      return <IonIcon icon={closeCircleOutline} style={{ color: '#ff4d4f' }} />;
    }
    // Validation failure statuses - show warning icon
    if (s === 'insufficient_context' || s === 'blocked') {
      return <IonIcon icon={alertCircleOutline} style={{ color: '#faad14' }} />;
    }
    if (s === 'running' || s === 'in_progress' || s === 'queued' || s === 'pending') {
      return <IonSpinner style={{ width: '16px', height: '16px' }} />;
    }
    return <IonIcon icon={timeOutline} style={{ color: '#8c8c8c' }} />;
  };
  
  // Normalize status for display
  const getDisplayStatus = (analysis) => {
    const s = getEffectiveStatus(analysis);
    if (s === 'completed' || s === 'success') return 'Completed';
    if (s === 'cancelled') return 'Cancelled';
    if (s === 'failed' || s === 'error') return 'Failed';
    if (s === 'insufficient_context') return 'Insufficient Context';
    if (s === 'blocked') return 'Needs Clarification';
    if (s === 'running' || s === 'in_progress') return 'Running';
    if (s === 'queued' || s === 'pending') return 'Queued';
    return analysis.status || 'Unknown';
  };

  // Get story points from latest analysis (backend extracts this for us)
  const storyPointEstimate = analyses[0]?.storyPoints ? {
    points: analyses[0].storyPoints,
    reasoning: analyses[0].storyPointReasoning,
    confidence: analyses[0].storyPointConfidence
  } : null;

  if (loading) {
    return (
      <div className="ticket-details-loading">
        <IonSpinner />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <NotFoundPage
        code={error ? '500' : '404'}
        title={error ? 'Failed to load ticket' : 'Ticket not found'}
        subtitle={error || "This ticket doesn't exist, or it may have been deleted. Check the URL or browse your project's tickets."}
        primaryAction={{ label: 'Back to tickets', to: `/projects/${projectId}/tickets`, icon: arrowBackOutline }}
        secondaryAction={{ label: 'All tickets', to: `/projects/${projectId}/tickets`, icon: ticketOutline }}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{ticket.key}: {ticket.summary} - Zibby</title>
      </Helmet>
      <div className="ticket-details-page breadcrumb-spacing">
        {/* Inline suggestions action bar */}
        {suggestedChanges.length > 0 && (
          <div className="inline-suggestions-bar">
            <span className="inline-suggestions-bar-label">
              {applyingChanges
                ? 'Applying changes...'
                : `AI Suggestions (${suggestedChanges.filter(c => c.status === 'pending').length} pending)`
              }
            </span>
            <div className="inline-suggestions-bar-actions">
              {hasPendingChanges && !applyingChanges && (
                <DiffActionBtns
                  onAccept={handleAcceptAll}
                  onReject={handleRejectAll}
                  acceptLabel="Keep All"
                  rejectLabel="Undo All"
                  acceptIcon={checkmarkCircleOutline}
                  rejectIcon={arrowUndoOutline}
                />
              )}
              {applyingChanges && <IonSpinner name="crescent" style={{ width: 18, height: 18 }} />}
            </div>
            {applyError && <div className="inline-bar-error">{applyError}</div>}
          </div>
        )}

        {/* Header */}
        <TicketTitleRow
          title={ticket.localOverrides?.summary || ticket.summary}
          issueType={ticket.issueType}
          jiraUrl={ticket.jiraUrl}
          titleDiff={(() => {
            const summaryChange = getFieldChange('summary');
            if (!summaryChange || summaryChange.status === 'rejected') return null;
            if (summaryChange.status === 'accepted') return null;
            const diffOps = computeWordDiff(summaryChange.original, summaryChange.suggested);
            return (
              <HoverPopover
                className="title-inline-diff-wrapper"
                popover={
                  <>
                    {summaryChange.reasoning && (
                      <div className="title-diff-reasoning">{summaryChange.reasoning}</div>
                    )}
                    <DiffActionBtns
                      onAccept={() => handleAcceptChange(summaryChange.id)}
                      onReject={() => handleRejectChange(summaryChange.id)}
                      disabled={applyingChanges}
                      className="title-diff-actions"
                    />
                  </>
                }
              >
                <h1 className="shared-ticket-title title-has-diff">
                  {diffOps.map((op, idx) => {
                    if (op.type === 'equal') return <span key={idx} className="idiff-equal">{op.value}</span>;
                    if (op.type === 'removed') return <span key={idx} className="idiff-removed">{op.value}</span>;
                    if (op.type === 'added') return <span key={idx} className="idiff-added">{op.value}</span>;
                    return null;
                  })}
                </h1>
              </HoverPopover>
            );
          })()}
          actions={
            <div className="ticket-header-actions">
              {latestAnalysis && ['blocked', 'insufficient_context'].includes(getEffectiveStatus(latestAnalysis)) && (
                <span className={`analysis-status-badge ${getEffectiveStatus(latestAnalysis)}`}>
                  {getDisplayStatus(latestAnalysis)}
                </span>
              )}
            </div>
          }
        />

        {/* Tabs */}
        <div className="ticket-tabs">
          <button 
            className={`ticket-tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => handleTabChange('summary')}
          >
            Summary
          </button>
          <button 
            className={`ticket-tab ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => handleTabChange('analysis')}
          >
            Analysis
          </button>
        </div>

        <div className="ticket-content">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="ticket-main">
              {/* Local Overrides Banner - Show if ticket has applied changes */}

              {/* Description */}
              <section className="ticket-section">
                <h2 className="section-title">Description</h2>
                <div className="section-content">
                  <DescriptionWithInlineDiff
                    html={ticket.descriptionHtml}
                    projectId={projectId}
                    changes={(() => {
                      const currentAnalysisId = latestAnalysis?.executionId || latestAnalysis?.id;
                      const overrideIsFromThisAnalysis = ticket.appliedAnalysisId === currentAnalysisId;
                      if (ticket.localOverrides?.description && overrideIsFromThisAnalysis) return [];
                      return suggestedChanges.filter(c => c.field === 'description');
                    })()}
                    onAccept={handleAcceptChange}
                    onReject={handleRejectChange}
                    disabled={applyingChanges}
                    localOverrides={ticket.localOverrides}
                  />
                </div>
              </section>

              {/* Story Point Estimate */}
              <section className="ticket-section story-points-section">
                <h2 className="section-title">
                  Story Point Estimate
                </h2>
                <div className="section-content">
                  {storyPointEstimate ? (
                    <div className="story-points-container">
                      <div className="story-points-value">
                        {storyPointEstimate.points} points
                      </div>
                      <div className="story-points-confidence">
                        Confidence: <span className={`confidence-${storyPointEstimate.confidence}`}>
                          {storyPointEstimate.confidence}
                        </span>
                      </div>
                      <div
                        className="story-points-reasoning"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(storyPointEstimate.reasoning) }}
                      />
                    </div>
                  ) : (
                    <div className="placeholder-text">
                      No story point estimate yet.
                    </div>
                  )}
                </div>
              </section>

              {/* Code changes and test cases */}
              <section className="ticket-section">
                <h2 className="section-title">
                  {analyses.length > 0 && analyses[0] ? (
                    <>
                      Code changes and test cases - {analyses[0].name || `Analysis ${analyses[0].executionId?.slice(0, 8) || ''}`}
                    </>
                  ) : (
                    'Generated Source Code'
                  )}
                </h2>
                <div className="section-content">
                  {analysesLoading ? (
                    <div className="placeholder-text">
                      <IonSpinner />
                    </div>
                  ) : analyses.length > 0 && analyses[0]?.artifacts?.codeImplementation?.diff ? (
                    (() => {
                      const sections = splitCodeImplByRepo(analyses[0].artifacts.codeImplementation, analyses[0].repos);
                      return (
                        <div className={`analysis-artifacts ${sections.length > 1 ? 'multi-repo-group' : ''}`}>
                          {sections.map((section) => (
                            <div className="artifact-section" key={section.repoName || 'default'}>
                              <CodeDiffViewer
                                diff={section.diff}
                                fileContents={section.fileContents}
                                changedFiles={section.changedFiles}
                                showFileList={true}
                                showFileTree={true}
                                defaultView="side-by-side"
                                title={section.repoName ? `Code Changes — ${section.repoName}` : 'Code Changes'}
                                headerActions={
                                  <button 
                                    className="create-pr-btn"
                                    onClick={() => handleCreatePR(section.repoName)}
                                    disabled={isCreatingRepo(section.repoName)}
                                    title={`Create a pull request for ${section.repoName || 'these changes'}`}
                                  >
                                    {isCreatingRepo(section.repoName) ? (
                                      <>
                                        <IonSpinner name="crescent" />
                                        Create Pull Request
                                      </>
                                    ) : (
                                      <>
                                        <IonIcon icon={gitBranchOutline} />
                                        Create Pull Request
                                      </>
                                    )}
                                  </button>
                                }
                              />
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="placeholder-text">
                      No generated code yet.
                    </div>
                  )}
                </div>
                {analyses.length > 0 && analyses[0]?.artifacts?.tests?.structured?.testCases?.length > 0 && (
                  <div className="section-content">
                    <TestCasesViewer
                      testCases={analyses[0].artifacts.tests.structured.testCases}
                      summary={analyses[0].artifacts.tests.structured.summary}
                      onChange={handleTestCasesChange}
                      executionId={analyses[0].executionId || analyses[0].id}
                      projectId={projectId}
                      aiAgent={projectData?.project?.aiAgent}
                      aiModel={projectData?.project?.aiModel}
                    />
                  </div>
                )}
              </section>

              {/* Labels - show overrides or pending suggestion */}
              {(ticket.localOverrides?.labels || (getFieldChange('labels') && getFieldChange('labels').status !== 'rejected')) && (
                <section className="ticket-section">
                  <h2 className="section-title">Labels</h2>
                  <div className="section-content">
                    {ticket.localOverrides?.labels ? (
                      <div className="accepted-labels">
                        {ticket.localOverrides.labels.map((label, i) => (
                          <span key={i} className="label-tag">{label}</span>
                        ))}
                      </div>
                    ) : (
                      <InlineSuggestion
                        change={getFieldChange('labels')}
                        onAccept={handleAcceptChange}
                        onReject={handleRejectChange}
                        disabled={applyingChanges}
                      />
                    )}
                  </div>
                </section>
              )}

              {/* Attachments */}
              <section className="ticket-section">
                <h2 className="section-title">
                  Attachments
                  {ticket.attachments?.length > 0 && (
                    <span className="section-count">{ticket.attachments.length}</span>
                  )}
                  {refreshing && <IonSpinner className="section-spinner" />}
                </h2>
                <div className="section-content">
                  {ticket.attachments?.length > 0 ? (
                    <div className="attachments-grid">
                      {ticket.attachments.map((att, index) => {
                        // Get index among image attachments for gallery
                        const imageAttachments = ticket.attachments.filter(a => isImage(a.mimeType));
                        const imageIndex = imageAttachments.findIndex(a => a.id === att.id);
                        
                        return (
                          <div 
                            key={att.id} 
                            className={`attachment-item ${isImage(att.mimeType) ? 'attachment-image' : ''}`}
                            onClick={async () => {
                              if (isImage(att.mimeType)) {
                                // Open gallery for images
                                setGalleryIndex(imageIndex);
                                setGalleryOpen(true);
                              } else {
                                // Download non-image files
                                try {
                                  const response = await fetch(getAttachmentUrl(projectId, att.id), {
                                    headers: { 'Authorization': `Bearer ${getAuthToken()}` }
                                  });
                                  const blob = await response.blob();
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = att.filename;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                } catch (err) {
                                  console.error('Download failed:', err);
                                }
                              }
                            }}
                          >
                            {isImage(att.mimeType) ? (
                              <div className="attachment-preview">
                                <AuthImage 
                                  src={getAttachmentUrl(projectId, att.id, true)} 
                                  alt={att.filename}
                                />
                              </div>
                            ) : isVideo(att.mimeType) ? (
                              <div className="attachment-preview attachment-video">
                                <IonIcon icon={videocamOutline} />
                              </div>
                            ) : (
                              <div className="attachment-preview attachment-file">
                                <IonIcon icon={documentOutline} />
                              </div>
                            )}
                            <div className="attachment-info">
                              <span className="attachment-name">{att.filename}</span>
                              <span className="attachment-meta">
                                {formatFileSize(att.size)}
                                {att.author && ` • ${att.author.displayName}`}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="placeholder-text">No attachments</div>
                  )}
                </div>
              </section>

              {/* Subtasks */}
              <section className="ticket-section">
                <h2 className="section-title">Subtasks</h2>
                <div className="section-content">
                  {ticket.subtasks?.length > 0 ? (
                    <div className="subtasks-list">
                      {ticket.subtasks.map((sub) => (
                        <div key={sub.key} className="subtask-item">
                          {sub.issueType?.iconUrl && (
                            <img src={sub.issueType.iconUrl} alt="" className="subtask-icon" />
                          )}
                          <span className="subtask-key">{sub.key}</span>
                          <span className="subtask-summary">{sub.summary}</span>
                          <span className={`subtask-status status-${sub.statusCategory}`}>
                            {sub.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="placeholder-text">No subtasks</div>
                  )}
                </div>
              </section>

              {/* Linked Work Items */}
              <section className="ticket-section">
                <h2 className="section-title">Linked work items</h2>
                <div className="section-content">
                  {ticket.linkedIssues?.length > 0 ? (
                    <div className="linked-list">
                      {ticket.linkedIssues.map((link) => {
                        const linkedIssue = link.inwardIssue || link.outwardIssue;
                        const linkText = link.inwardIssue ? link.inward : link.outward;
                        return linkedIssue ? (
                          <div key={link.id} className="linked-item">
                            <span className="link-type">{linkText}</span>
                            {linkedIssue.issueType?.iconUrl && (
                              <img src={linkedIssue.issueType.iconUrl} alt="" className="linked-icon" />
                            )}
                            <span className="linked-key">{linkedIssue.key}</span>
                            <span className="linked-summary">{linkedIssue.summary}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <div className="placeholder-text">No linked issues</div>
                  )}
                </div>
              </section>

              {/* Comments / Activity */}
              <section className="ticket-section">
                <h2 className="section-title">
                  Activity
                  {ticket.comments?.length > 0 && (
                    <span className="section-count">{ticket.comments.length}</span>
                  )}
                </h2>
                <div className="section-content">
                  {ticket.comments?.length > 0 ? (
                    <div className="comments-list">
                      {ticket.comments.map((comment) => (
                        <div key={comment.id} className="comment-item">
                          <div className="comment-header">
                            {comment.author?.avatarUrl && (
                              <img 
                                src={comment.author.avatarUrl} 
                                alt="" 
                                className="comment-avatar"
                              />
                            )}
                            <span className="comment-author">
                              {comment.author?.displayName || 'Unknown'}
                            </span>
                            <span className="comment-date">
                              {formatRelativeTime(comment.created)}
                            </span>
                          </div>
                          <div className="comment-body">
                            {comment.bodyHtml ? (
                              <AuthHtmlContent 
                                html={comment.bodyHtml}
                                projectId={projectId}
                              />
                            ) : (
                              <div>{extractAdfText(comment.body)}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="placeholder-text">No comments</div>
                  )}
                </div>
              </section>
            </div>
          )}

          {/* Analysis Tab */}
          {activeTab === 'analysis' && (
            <div className="ticket-main">
              {analysesLoading ? (
                <div className="analyses-loading">
                  <IonSpinner />
                </div>
              ) : analyses.length === 0 ? (
                <div className="analyses-empty">
                  <IonIcon icon={analyticsOutline} />
                  <p>No past analyses for this ticket</p>
                </div>
              ) : (
                <div className="analyses-list">
                  {analyses.map((analysis) => (
                    <div 
                      key={analysis.executionId || analysis.id}
                      className="analysis-item"
                    >
                      <div className="analysis-item-left">
                        {getStatusIcon(analysis)}
                        <div className="analysis-item-info">
                          <span className="analysis-item-name">
                            {analysis.name || `Analysis ${analysis.executionId?.slice(0, 8) || ''}`}
                          </span>
                          <span className="analysis-item-date">
                            {formatRelativeTime(analysis.createdAt || analysis.startedAt)}
                          </span>
                        </div>
                      </div>
                      <div className="analysis-item-right">
                        <span className={`analysis-status ${getEffectiveStatus(analysis)}`}>
                          {getDisplayStatus(analysis)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Gallery */}
      {galleryOpen && ticket?.attachments && (
        <ImageGallery
          images={ticket.attachments.filter(a => isImage(a.mimeType))}
          initialIndex={galleryIndex}
          onClose={() => setGalleryOpen(false)}
          projectId={projectId}
        />
      )}
    </>
  );
};

export default TicketDetailsPage;
